import React, { useMemo } from "react";
import { Field, formValueSelector } from "redux-form";
import PropTypes from "prop-types";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Space, Table } from "antd";
import { useSelector } from "react-redux";

import { required } from "utils/formValidations";
import { renderCascader, renderInput } from "components/FormItem";
import { UNIT_LABELS_BY_VALUE } from "constants/index";
import { formatPrice, singletonCurrency } from "utils/helpers";

import { cascadeCategoriesForProductsData } from "../../shared/utils";

const formItemLayout = {
  labelCol: { span: 0 },
  wrapperCol: { span: 24 },
  style: { margin: 0 },
};

const capPrecision = (value, precision) => parseFloat(value.toFixed(precision));

const parseFloatValue = (precision) => (value) => {
  const parsedValue = parseFloat(value);
  return parsedValue || parsedValue === 0 ? capPrecision(parsedValue, precision) : "";
};

const ProductFields = ({
  t, fields, changeField, disabled, optionsData, meta,
}) => {
  const contents = useMemo(() => {
    if (!optionsData) return [];
    return cascadeCategoriesForProductsData(optionsData)
      .filter((cat) => ["products", "ingredients"].includes(cat.value));
  }, [optionsData]);

  const onSelectContent = (idx, value) => {
    const product = value.reduce(
      (val, pathItem) => val.children.find((c) => c.value === pathItem),
      { children: contents },
    );

    changeField(`${fields.name}[${idx}].product`, product);
    changeField(`${fields.name}[${idx}].product_id`, product.value);
  };

  const onQuantityChange = (idx, value) => {
    const quantity = value || 0;
    const { price, total_price } = fields.get(idx);
    if (price || price === 0) {
      changeField(`${fields.name}[${idx}].total_price`, capPrecision(price * quantity, 2));
    } else if (total_price || total_price === 0) {
      const newPrice = quantity === 0 ? "" : capPrecision(total_price / quantity, 2);
      changeField(`${fields.name}[${idx}].price`, newPrice);
    }
  };

  const onPriceChange = (idx, value) => {
    const price = value || 0;
    const { quantity, total_price } = fields.get(idx);
    if (quantity || quantity === 0) {
      changeField(`${fields.name}[${idx}].total_price`, capPrecision(price * quantity, 2));
    } else if (total_price || total_price === 0) {
      const newQuantity = price === 0 ? "" : capPrecision(total_price / price, 3);
      changeField(`${fields.name}[${idx}].quantity`, newQuantity);
    }
  };

  const onTotalPriceChange = (idx, value) => {
    const totalPrice = value || 0;
    const { quantity, price } = fields.get(idx);
    if (quantity || quantity === 0) {
      const newPrice = quantity === 0 ? "" : capPrecision(totalPrice / quantity, 2);
      changeField(`${fields.name}[${idx}].price`, newPrice);
    } else if (price || price === 0) {
      const newQuantity = price === 0 ? "" : capPrecision(totalPrice / price, 3);
      changeField(`${fields.name}[${idx}].quantity`, newQuantity);
    }
  };

  const selector = formValueSelector(meta.form);
  const deliveryProducts = useSelector((state) => selector(state, fields.name));
  const totalPrice = (deliveryProducts || [])
    .reduce((acc, { total_price }) => acc + (total_price || 0), 0);

  return (
    <Table
      rowKey={(field) => fields.getAll().indexOf(field)}
      dataSource={fields.getAll()}
      size="small"
      pagination={false}
      locale={{ emptyText: t("warehouse.Deliveries.Form.ProductFields.EmptyProducts") }}
      summary={() => (
        <>
          {!disabled && (
            <Table.Summary.Row style={{ backgroundColor: "#fff" }}>
              <Table.Summary.Cell colSpan={5}>
                <Space>
                  <Button type="dashed" icon={<PlusOutlined />} onClick={() => fields.push({})}>
                    {t("warehouse.Deliveries.Form.ProductFields.AddProduct")}
                  </Button>
                  {fields.length > 1 && (
                    <Button type="link" danger onClick={() => fields.removeAll()}>
                      {t("DeleteAll")}
                    </Button>
                  )}
                </Space>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
          <Table.Summary.Row>
            <Table.Summary.Cell colSpan={3}>
              {t("SummaryTotal")}
            </Table.Summary.Cell>
            <Table.Summary.Cell colSpan={2}>
              {formatPrice(totalPrice)}
            </Table.Summary.Cell>
          </Table.Summary.Row>
        </>
      )}
    >
      <Table.Column
        title={t("warehouse.Deliveries.Form.ProductFields.Name")}
        dataIndex="content_path"
        render={(_c, _f, index) => (
          <Field
            name={`${fields.name}[${index}].content_path`}
            disabled={disabled}
            style={{ width: 240 }}
            component={renderCascader}
            onChange={(v) => onSelectContent(index, v)}
            validate={required}
            placeholder={t("warehouse.Deliveries.Form.ProductFields.ChooseProduct")}
            formItemProps={formItemLayout}
            options={contents}
            showSearch
          />
        )}
      />
      <Table.Column
        title={t("warehouse.Deliveries.Form.ProductFields.Quantity")}
        dataIndex="product"
        render={(product, _p, index) => (
          <Field
            name={`${fields.name}[${index}].quantity`}
            disabled={disabled}
            component={renderInput}
            onChange={(_e, v) => onQuantityChange(index, v)}
            validate={required}
            type="number"
            min={0}
            step={0.001}
            parse={parseFloatValue(3)}
            formItemProps={formItemLayout}
            addonAfter={product && UNIT_LABELS_BY_VALUE[product.unit]}
          />
        )}
      />
      <Table.Column
        title={t("warehouse.Deliveries.Form.ProductFields.UnitPrice")}
        dataIndex="product"
        render={(product, _p, index) => (
          <Field
            name={`${fields.name}[${index}].price`}
            disabled={disabled}
            component={renderInput}
            onChange={(_e, v) => onPriceChange(index, v)}
            validate={required}
            type="number"
            min={0}
            step={0.01}
            parse={parseFloatValue(2)}
            formItemProps={formItemLayout}
            addonAfter={singletonCurrency.getCurrency()}
          />
        )}
      />
      <Table.Column
        title={t("warehouse.Deliveries.Form.ProductFields.TotalAmount")}
        dataIndex="product"
        render={(product, _p, index) => (
          <Field
            name={`${fields.name}[${index}].total_price`}
            disabled={disabled}
            component={renderInput}
            onChange={(_e, v) => onTotalPriceChange(index, v)}
            validate={required}
            type="number"
            min={0}
            step={0.01}
            parse={parseFloatValue(2)}
            formItemProps={formItemLayout}
            addonAfter={singletonCurrency.getCurrency()}
          />
        )}
      />
      {disabled || (
        <Table.Column
          dataIndex="content_path"
          render={(_c, _p, index) => (
            <Button danger onClick={() => fields.remove(index)}>
              <DeleteOutlined />
            </Button>
          )}
        />
      )}
    </Table>
  );
};

ProductFields.propTypes = {
  t: PropTypes.func.isRequired,
  changeField: PropTypes.func.isRequired,
  fields: PropTypes.object.isRequired,
  optionsData: PropTypes.object,
  meta: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

export default ProductFields;
